/**
 * SessionExpiryWatcher — proactive session-renewal + expiry UX.
 *
 * Solves the "user signs in at 9 AM, comes back from a long meeting
 * at 5 PM, hits Save, and the request 401s without warning"
 * problem. The api now surfaces `sessionExpiresAt` on every /me
 * response (and the Bearer-path middleware applies sliding-window
 * extension when the user is active), so we can:
 *
 *   1. Warn the user with a small banner when remaining time
 *      drops below WARN_THRESHOLD_MIN. Banner gives them a
 *      one-click "Stay signed in" action that re-fetches /me
 *      (which extends the session) AND a Dismiss (X) so they
 *      can hide it if they're aware and don't want it nagging.
 *
 *   2. Auto-refresh /me every PROACTIVE_REFRESH_MIN minutes
 *      while the tab is visible, so an actively-used session
 *      slides forward continuously without the user noticing.
 *
 *   3. When the expiry is genuinely past, fire a one-shot
 *      refresh() — /me will 401, AuthContext clears the user,
 *      App re-renders SignInPage. NO window.location.replace
 *      (the previous version did that and caused a flashing
 *      loop because React Query held stale user data with the
 *      past expiry, the watcher kept firing across reloads).
 *
 * The watcher mounts at the app root (above all routes) so it
 * runs everywhere the user is signed in. It does nothing until
 * `user.sessionExpiresAt` is populated by the first /me call.
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';

/** Minutes-remaining threshold below which we render the banner.
 *  At 14-day TTL with sliding renewal, an active user effectively
 *  never sees this banner. It only fires when the user has been
 *  away long enough that their session is genuinely about to die
 *  AND they've come back without refreshing. 60 min gives enough
 *  warning to click "Stay signed in" without the banner being
 *  visually noisy on long-running sessions. */
const WARN_THRESHOLD_MIN = 60;

/** How often we silently re-fetch /me to extend the session
 *  while the tab is visible. At 14-day TTL we don't need to
 *  refresh aggressively — once every 6 hours is plenty to keep
 *  an active user sliding forward, and minimizes background
 *  request noise. */
const PROACTIVE_REFRESH_MIN = 6 * 60;

/** Tick interval for the countdown display + threshold check.
 *  Coarse — once a minute is fine. */
const TICK_INTERVAL_MS = 60_000;

interface BannerState {
  show: boolean;
  minutesRemaining: number;
  expired: boolean;
}

function computeBannerState(
  expiresAt: string | null | undefined,
): BannerState {
  if (!expiresAt) return { show: false, minutesRemaining: 0, expired: false };
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  const minutesRemaining = Math.max(0, Math.floor(remainingMs / 60_000));
  if (remainingMs <= 0) {
    return { show: true, minutesRemaining: 0, expired: true };
  }
  return {
    show: minutesRemaining <= WARN_THRESHOLD_MIN,
    minutesRemaining,
    expired: false,
  };
}

export function SessionExpiryWatcher() {
  const { user, refresh } = useAuth();
  const [, forceTick] = useState(0);
  // User-controlled dismissal — when they click X, we hide the
  // banner for THIS session-expiry window. We re-arm it whenever
  // sessionExpiresAt changes (i.e. after a successful refresh
  // bumped the expiry forward), so a future near-expiry banner
  // for the NEW window is shown again. Tracking the dismissed
  // expiry timestamp so we don't re-show until the underlying
  // value actually changes.
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  // One-shot expired-refresh guard so we don't fire refresh()
  // repeatedly while the watcher is stuck on the expired state
  // waiting for AuthContext to drop the user.
  const expiredRefreshFiredRef = useRef(false);
  // Belt-and-suspenders against any future remount-loop bug:
  // even if the ref above gets blown away (component unmounted
  // and remounted), this module-scope timestamp survives, and
  // we refuse to fire refresh() more than once per 10s no matter
  // what triggered it. If the loop ever recurs the user gets a
  // background hum instead of a tab-killing flood.
  // Using a ref to access the module-scope tracker keeps the
  // throttle scoped per-AuthProvider, not truly global.
  const lastExpiredRefreshAtRef = useRef<number>(0);

  // Proactive refresh — every PROACTIVE_REFRESH_MIN while the
  // tab is visible. Pauses when the tab is hidden so background
  // tabs don't spam the api.
  useEffect(() => {
    if (!user) return;
    let intervalId: number | null = null;

    const start = () => {
      stop();
      intervalId = window.setInterval(
        () => {
          if (document.visibilityState === 'visible') {
            void refresh();
          }
        },
        PROACTIVE_REFRESH_MIN * 60_000,
      );
    };
    const stop = () => {
      if (intervalId !== null) window.clearInterval(intervalId);
      intervalId = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Tab just became visible — refresh once immediately,
        // then resume the regular interval. Catches the case
        // where the user came back after a long absence.
        void refresh();
        start();
      } else {
        stop();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, refresh]);

  // Tick the banner state once a minute so the countdown moves.
  useEffect(() => {
    if (!user?.sessionExpiresAt) return;
    const id = window.setInterval(() => forceTick((n) => n + 1), TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [user?.sessionExpiresAt]);

  // Re-arm the dismiss state whenever the underlying expiry
  // changes — a refresh that bumps the expiry should resurrect
  // the banner for any future near-expiry state.
  useEffect(() => {
    if (
      user?.sessionExpiresAt &&
      dismissedFor &&
      dismissedFor !== user.sessionExpiresAt
    ) {
      setDismissedFor(null);
    }
  }, [user?.sessionExpiresAt, dismissedFor]);

  // Reset the expired-refresh guard whenever the expiry value
  // changes (so a future expired state will fire one shot
  // again).
  useEffect(() => {
    expiredRefreshFiredRef.current = false;
  }, [user?.sessionExpiresAt]);

  if (!user) return null;
  const state = computeBannerState(user.sessionExpiresAt);

  // Hard-expiry: fire a one-shot refresh. /me will 401,
  // AuthContext clears the user, App.tsx re-renders SignInPage.
  // No window.location.replace — that previously caused a
  // flashing loop because React Query held stale user data with
  // the past expiry across reloads, and the watcher kept
  // re-firing the redirect on every mount.
  if (state.expired) {
    const now = Date.now();
    const sinceLast = now - lastExpiredRefreshAtRef.current;
    // Two-layer guard: per-mount one-shot ref (handles the
    // happy case) AND per-10s throttle (handles any future
    // remount-loop regression). Either one returning
    // "already fired" suppresses the call.
    if (!expiredRefreshFiredRef.current && sinceLast > 10_000) {
      expiredRefreshFiredRef.current = true;
      lastExpiredRefreshAtRef.current = now;
      void refresh();
    }
    return null;
  }

  // Dismissed for this expiry window — hide until the underlying
  // sessionExpiresAt changes (next successful refresh).
  if (dismissedFor && dismissedFor === user.sessionExpiresAt) return null;

  if (!state.show) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 right-4 z-[100] flex max-w-sm items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-lg"
    >
      <div className="flex-1">
        <div className="font-semibold">Session ending soon</div>
        <div className="mt-0.5 text-xs text-amber-800">
          You&rsquo;ll be signed out in {state.minutesRemaining} minute
          {state.minutesRemaining === 1 ? '' : 's'} unless you stay
          active.
        </div>
      </div>
      <button
        type="button"
        onClick={() => void refresh()}
        className="shrink-0 rounded-full bg-amber-700 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-800"
      >
        Stay signed in
      </button>
      <button
        type="button"
        onClick={() => setDismissedFor(user.sessionExpiresAt ?? '')}
        title="Dismiss until session is renewed"
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-amber-900/55 transition-colors hover:bg-amber-100 hover:text-amber-900"
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}
